import { useEffect, useRef, FC } from 'react';

/**
 * DocumentTitle component props interface
 */
interface DocumentTitleProps {
  /**
   * The string value to set as the document title
   */
  value: string;
  
  /**
   * Whether to restore the previous document title when the component unmounts
   * @default true
   */
  restoreLast?: boolean;
}

/**
 * DocumentTitle component
 * 
 * A utility component that sets the document title (browser tab title)
 * when the component mounts or when the value prop changes.
 * This component doesn't render anything in the DOM.
 * 
 * @example
 * ```tsx
 * <DocumentTitle value="Dashboard - Smarter Services" />
 * ```
 * 
 * @example
 * ```tsx
 * <DocumentTitle value="Form - Smarter Services" restoreLast={false} />
 * ```
 * 
 * @param {DocumentTitleProps} props - Component props
 * @returns {null} - This component doesn't render anything
 */
const DocumentTitle: FC<DocumentTitleProps> = ({ value, restoreLast = true }) => {
  // Store the original title in a ref so it's only captured once on mount
  const originalTitleRef = useRef<string>(document.title);
  
  useEffect(() => {
    // Update the document title when the component mounts or when value changes
    document.title = value;
    
    // Restore the previous title when the component unmounts if restoreLast is true
    return () => {
      if (restoreLast) {
        document.title = originalTitleRef.current;
      }
    };
  }, [value]); // Only re-run when value changes, not when restoreLast changes

  // This component doesn't render anything to the DOM
  return null;
};

export default DocumentTitle;